/**
 * MapWarningChip — Day 76 P5.1 MAP violation warning chip.
 *
 * Renders inline next to sale_price when product_profitability.below_map ===
 * true per ADR 41 Shape B. Engine reports truth on sale_price; UI surfaces
 * the flag via this chip; buyer decides (list at MAP, skip the row, accept
 * sub-MAP). Engine does NOT alter sale_price computation.
 *
 * 3-state gate (per schema.sql COMMENT on product_profitability.below_map):
 *   NULL  — engine has not evaluated (brand not enforcing OR no map_price);
 *           no badge (caller responsibility — don't render this component)
 *   TRUE  — sale_price < map_price under enforcing brand; render this chip
 *   FALSE — evaluated clean (sale_price >= map_price); no badge (caller
 *           responsibility — don't render this component)
 *
 * Caller is responsible for the 3-state gate: render this component ONLY
 * when below_map === true. Keeps the component contract simple.
 *
 * Tooltip mechanism: native HTML title attribute (V1 lean per build prompt).
 * Day 80 P5.5 / partner UAT signal candidate for richer overlay.
 *
 * Color: text-warning per tailwind theme (Arbi warning palette: #fbbf24).
 * Icon: Lucide AlertTriangle size=14 mirrors existing Arbi pattern
 * (ParamsSidebar.tsx L195, FulfillmentOrderDetail.tsx L728).
 *
 * Consumers (Day 76 P5.1 Phase 6 closure):
 *   - BuyingSession.tsx sale_price column cellRenderer (6D)
 *   - DetailPanel.tsx Channel Comparison sale_price cell (6E)
 *   - DetailPanel.tsx Cost Breakdown header (6F)
 */

import type { JSX } from 'react';
import { AlertTriangle } from 'lucide-react';

interface MapWarningChipProps {
  mapPrice?: number | null;
  brandName?: string | null;
}

function fmt$(value: number | null | undefined): string {
  if (value == null) return '';
  return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function MapWarningChip({ mapPrice, brandName }: MapWarningChipProps): JSX.Element {
  const titleParts: string[] = ['Sale price below MAP'];
  if (mapPrice != null) titleParts.push(`MAP: ${fmt$(mapPrice)}`);
  if (brandName) titleParts.push(`Brand: ${brandName}`);
  titleParts.push('(brand enforces MAP)');
  const title = titleParts.join(' · ');
  const ariaLabel = brandName ? `MAP violation: ${brandName} enforces MAP` : 'MAP violation';
  return (
    <span className="inline-flex items-center text-warning" title={title} aria-label={ariaLabel}>
      <AlertTriangle size={14} aria-hidden />
    </span>
  );
}
